// src/components/Admin/ThemeRegistryManagement.js
import React, { useState } from 'react';
import { Box, Typography, Button } from '@mui/material';

const ThemeRegistryManagement = () => {
    const [themeRegistry, setThemeRegistry] = useState(null);
    const [registryStats, setRegistryStats] = useState(null);

    return (
        <Box>
            <Typography variant="h5" gutterBottom>
                Theme Registry Management
            </Typography>
            <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary' }}>
                Manage canonical themes used for Industry Insights aggregation. 
                Approve pending suggestions or add new themes manually.
            </Typography>

            {/* View Registry and Add Theme Buttons */}
            <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                <Button
                    variant="outlined"
                    color="primary"
                    onClick={async () => {
                        try {
                            const { getFunctions, httpsCallable } = await import('firebase/functions');
                            const functions = getFunctions();
                            const manageFn = httpsCallable(functions, 'manageThemeRegistry');

                            console.log('📋 Fetching theme registry...');
                            const result = await manageFn({ command: 'view' });
                            console.log('✅ Registry:', result.data);

                            setThemeRegistry(result.data.registry);
                            setRegistryStats(result.data.stats);
                        } catch (error) {
                            console.error('❌ Error:', error);
                            alert(`Error: ${error.message}`);
                        }
                    }}
                >
                    📋 View Registry
                </Button>

                <Button
                    variant="outlined"
                    color="primary"
                    onClick={async () => {
                        const newTheme = prompt('Enter new canonical theme (lowercase, 2-4 words):');
                        if (!newTheme) return;

                        try {
                            const { getFunctions, httpsCallable } = await import('firebase/functions');
                            const functions = getFunctions();
                            const manageFn = httpsCallable(functions, 'manageThemeRegistry');

                            const result = await manageFn({ command: 'add', theme: newTheme });
                            console.log('✅ Result:', result.data);
                            alert(result.data.message);

                            // Refresh registry
                            const refreshResult = await manageFn({ command: 'view' });
                            setThemeRegistry(refreshResult.data.registry);
                            setRegistryStats(refreshResult.data.stats);
                        } catch (error) {
                            console.error('❌ Error:', error);
                            alert(`Error: ${error.message}`);
                        }
                    }}
                >
                    ➕ Add Theme
                </Button>
            </Box>

            {/* Registry Display */}
            {themeRegistry && (
                <Box>
                    {/* Approved Themes */}
                    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                        Approved Themes ({registryStats?.approvedCount || 0}):
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 3 }}>
                        {themeRegistry.canonicalThemes?.map((theme, idx) => (
                            <Box
                                key={idx}
                                sx={{
                                    px: 1.5,
                                    py: 0.5,
                                    bgcolor: '#e1bee7',
                                    borderRadius: 1,
                                    fontSize: '0.85rem',
                                    cursor: 'pointer',
                                    '&:hover': { bgcolor: '#ce93d8' }
                                }}
                                onClick={async () => {
                                    if (!window.confirm(`Remove theme "${theme}"?`)) return;
                                    try {
                                        const { getFunctions, httpsCallable } = await import('firebase/functions');
                                        const functions = getFunctions();
                                        const manageFn = httpsCallable(functions, 'manageThemeRegistry');
                                        const result = await manageFn({ command: 'remove', theme });
                                        alert(result.data.message);
                                        // Refresh registry
                                        const refreshResult = await manageFn({ command: 'view' });
                                        setThemeRegistry(refreshResult.data.registry);
                                        setRegistryStats(refreshResult.data.stats);
                                    } catch (error) {
                                        alert(`Error: ${error.message}`);
                                    }
                                }}
                            >
                                {theme}
                            </Box>
                        ))}
                    </Box>

                    {/* Pending Suggestions */}
                    {themeRegistry.pendingThemes?.length > 0 && (
                        <>
                            <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold', color: 'warning.main' }}>
                                Pending Suggestions ({registryStats?.pendingCount || 0}):
                            </Typography>
                            {themeRegistry.pendingThemes.map((pending, idx) => (
                                <Box
                                    key={idx}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                        mb: 1,
                                        p: 1.5,
                                        bgcolor: '#fff3e0',
                                        borderRadius: 1
                                    }}
                                >
                                    <Typography variant="body2" sx={{ flex: 1 }}>
                                        <strong>{pending.suggested}</strong>
                                        <Typography component="span" variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>
                                            (from: "{pending.rawTheme}" • occurrences: {pending.occurrences})
                                        </Typography>
                                    </Typography>
                                    <Button
                                        size="small"
                                        color="success"
                                        variant="contained"
                                        onClick={async () => {
                                            try {
                                                const { getFunctions, httpsCallable } = await import('firebase/functions');
                                                const functions = getFunctions();
                                                const manageFn = httpsCallable(functions, 'manageThemeRegistry');
                                                const result = await manageFn({ command: 'approve', pendingIndex: idx });
                                                alert(result.data.message);
                                                // Refresh
                                                const refreshResult = await manageFn({ command: 'view' });
                                                setThemeRegistry(refreshResult.data.registry);
                                                setRegistryStats(refreshResult.data.stats);
                                            } catch (error) {
                                                alert(`Error: ${error.message}`);
                                            }
                                        }}
                                    >
                                        ✓ Approve
                                    </Button>
                                    <Button
                                        size="small"
                                        color="error"
                                        variant="outlined"
                                        onClick={async () => {
                                            try {
                                                const { getFunctions, httpsCallable } = await import('firebase/functions');
                                                const functions = getFunctions();
                                                const manageFn = httpsCallable(functions, 'manageThemeRegistry');
                                                const result = await manageFn({ command: 'reject', pendingIndex: idx });
                                                alert(result.data.message);
                                                // Refresh
                                                const refreshResult = await manageFn({ command: 'view' });
                                                setThemeRegistry(refreshResult.data.registry);
                                                setRegistryStats(refreshResult.data.stats);
                                            } catch (error) {
                                                alert(`Error: ${error.message}`);
                                            }
                                        }}
                                    >
                                        ✗ Reject
                                    </Button>
                                </Box>
                            ))}
                        </>
                    )}

                    {/* No pending themes message */}
                    {themeRegistry.pendingThemes?.length === 0 && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                            No pending theme suggestions. New themes will appear here as AI encounters 
                            topics not in the approved list.
                        </Typography>
                    )}
                </Box>
            )}

            {/* Initial state - before clicking View Registry */}
            {!themeRegistry && (
                <Typography variant="body2" color="text.secondary">
                    Click "View Registry" to load the current canonical themes.
                </Typography>
            )}
        </Box>
    );
};

export default ThemeRegistryManagement;